import { call, put, takeLatest, take, fork, cancel } from 'redux-saga/effects';
import { EventChannel, eventChannel } from 'redux-saga';
import type { Purchase, PurchaseError } from '../../services/iap';
import { iapService } from '../../services/iap';
import {
  iapInitRequest,
  iapInitSuccess,
  iapInitFailure,
  iapFetchProductsRequest,
  iapFetchProductsSuccess,
  iapFetchProductsFailure,
  iapFetchSubscriptionsRequest,
  iapFetchSubscriptionsSuccess,
  iapFetchSubscriptionsFailure,
  iapPurchaseRequest,
  iapPurchaseFailure,
  iapPurchaseUpdate,
  iapPurchaseError,
  iapVerifyPurchaseRequest,
  iapVerifyPurchaseSuccess,
  iapVerifyPurchaseFailure,
  iapDisconnect,
} from './iapActions';
import { axiosInstance } from '../../utils/axiosInstance';
import { Platform } from 'react-native';

/**
 * Initialize IAP connection
 */
function* initializeIAP() {
  try {
    console.log('[IAP Saga] Initializing IAP...');
    const success: boolean = yield call([iapService, iapService.initialize]);

    if (success) {
      yield put(iapInitSuccess());
      console.log('[IAP Saga] IAP initialized successfully');
    } else {
      yield put(iapInitFailure('Failed to initialize IAP connection'));
    }
  } catch (error: any) {
    console.error('[IAP Saga] Init error:', error);
    yield put(iapInitFailure(error.message || 'Failed to initialize IAP'));
  }
}

/**
 * Fetch available products
 */
function* fetchProducts(action: ReturnType<typeof iapFetchProductsRequest>) {
  try {
    console.log('[IAP Saga] Fetching products:', action.payload);
    const products = yield call([iapService, iapService.getAvailableProducts], action.payload);
    yield put(iapFetchProductsSuccess(products));
    console.log('[IAP Saga] Products fetched:', products);
  } catch (error: any) {
    console.error('[IAP Saga] Fetch products error:', error);
    yield put(iapFetchProductsFailure(error.message || 'Failed to fetch products'));
  }
}

/**
 * Fetch available subscriptions
 */
function* fetchSubscriptions(action: ReturnType<typeof iapFetchSubscriptionsRequest>) {
  try {
    console.log('[IAP Saga] Fetching subscriptions:', action.payload);
    const subscriptions = yield call([iapService, iapService.getAvailableSubscriptions], action.payload);
    yield put(iapFetchSubscriptionsSuccess(subscriptions));
    console.log('[IAP Saga] Subscriptions fetched:', subscriptions);
  } catch (error: any) {
    console.error('[IAP Saga] Fetch subscriptions error:', error);
    yield put(iapFetchSubscriptionsFailure(error.message || 'Failed to fetch subscriptions'));
  }
}

/**
 * Purchase a product or subscription
 */
function* purchaseProduct(action: ReturnType<typeof iapPurchaseRequest>) {
  try {
    const { productId, pkg, isSubscription } = action.payload;
    console.log('[IAP Saga] Starting purchase:', { productId, pkg, isSubscription });

    if (isSubscription) {
      yield call([iapService, iapService.purchaseSubscription], productId);
    } else {
      yield call([iapService, iapService.purchaseProduct], productId);
    }

    console.log('[IAP Saga] Purchase request sent, waiting for update...');
    // The actual purchase update will come through the listener
  } catch (error: any) {
    console.error('[IAP Saga] Purchase error:', error);

    // Check if user cancelled
    if (error.code === 'E_USER_CANCELLED') {
      yield put(iapPurchaseFailure('Purchase cancelled by user'));
    } else {
      yield put(iapPurchaseFailure(error.message || 'Purchase failed'));
    }
  }
}

/**
 * Create event channel for purchase updates
 */
function createPurchaseChannel(): EventChannel<{ type: 'update' | 'error'; data: Purchase | PurchaseError }> {
  return eventChannel((emitter) => {
    iapService.setupPurchaseListeners(
      (purchase: Purchase) => {
        emitter({ type: 'update', data: purchase });
      },
      (error: PurchaseError) => {
        emitter({ type: 'error', data: error });
      }
    );

    // Return unsubscribe function
    return () => {
      iapService.removePurchaseListeners();
    };
  });
}

/**
 * Watch purchase channel
 */
function* watchPurchaseChannel() {
  const channel: EventChannel<any> = yield call(createPurchaseChannel);

  try {
    while (true) {
      const event: { type: 'update' | 'error'; data: Purchase | PurchaseError } = yield take(channel);

      if (event.type === 'update') {
        const purchase = event.data as Purchase;
        console.log('[IAP Saga] Purchase update received:', purchase);
        yield put(iapPurchaseUpdate(purchase));

        // Auto-verify the purchase
        // We need to get the package info - it should be in the state
        // For now, we'll trigger verification which will handle it
      } else if (event.type === 'error') {
        const error = event.data as PurchaseError;
        console.error('[IAP Saga] Purchase error received:', error);
        yield put(iapPurchaseError(error));
      }
    }
  } finally {
    channel.close();
  }
}

/**
 * Verify purchase with backend
 */
function* verifyPurchase(action: ReturnType<typeof iapVerifyPurchaseRequest>) {
  try {
    const { purchase, pkg } = action.payload;
    console.log('[IAP Saga] Verifying purchase with backend:', { purchase, pkg });

    // Prepare receipt data
    const receiptData = {
      transactionId: purchase.transactionId,
      productId: purchase.productId,
      purchaseToken: purchase.purchaseToken,
      transactionReceipt: (purchase as any).transactionReceipt, // iOS only
      platform: Platform.OS,
      packageId: pkg.id,
      packageType: pkg.type,
    };

    // Send to backend for verification
    const response = yield call(
      [axiosInstance, axiosInstance.post],
      '/api/store/iap/verify',
      receiptData
    );

    console.log('[IAP Saga] Backend verification response:', response.data);

    if (response.data.success) {
      // Finish the transaction
      if (pkg.type === 'CHIPS') {
        // Consumable - finish and consume
        yield call([iapService, iapService.finishTransaction], purchase);
        if (Platform.OS === 'android') {
          yield call([iapService, iapService.consumePurchaseAndroid], purchase);
        }
      } else if (pkg.type === 'VIP_MEMBERSHIP') {
        // Subscription - acknowledge
        yield call([iapService, iapService.finishTransaction], purchase);
        if (Platform.OS === 'android') {
          yield call([iapService, iapService.acknowledgePurchaseAndroid], purchase);
        }
      }

      yield put(iapVerifyPurchaseSuccess({
        purchase,
        pkg,
        newBalance: response.data.newBalance,
      }));

      console.log('[IAP Saga] Purchase verified and completed successfully');
    } else {
      throw new Error(response.data.message || 'Verification failed');
    }
  } catch (error: any) {
    console.error('[IAP Saga] Verification error:', error);
    yield put(iapVerifyPurchaseFailure({
      error: error.message || 'Failed to verify purchase',
      purchase: action.payload.purchase,
    }));
  }
}

/**
 * Disconnect IAP
 */
function* disconnectIAP() {
  try {
    console.log('[IAP Saga] Disconnecting IAP...');
    yield call([iapService, iapService.disconnect]);
    console.log('[IAP Saga] IAP disconnected');
  } catch (error) {
    console.error('[IAP Saga] Disconnect error:', error);
  }
}

/**
 * Root saga
 */
export function* iapSaga() {
  // Start listening to purchase updates
  const purchaseChannelTask = yield fork(watchPurchaseChannel);

  // Watch for actions
  yield takeLatest(iapInitRequest.type, initializeIAP);
  yield takeLatest(iapFetchProductsRequest.type, fetchProducts);
  yield takeLatest(iapFetchSubscriptionsRequest.type, fetchSubscriptions);
  yield takeLatest(iapPurchaseRequest.type, purchaseProduct);
  yield takeLatest(iapVerifyPurchaseRequest.type, verifyPurchase);
  yield takeLatest(iapDisconnect.type, function* () {
    yield cancel(purchaseChannelTask);
    yield call(disconnectIAP);
  });
}
